import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ApiService } from '../utils/api';
import { parseManualNumbers } from '../utils/lockerNumbers';
import { useToast } from './Toast';
import { Upload, Plus } from 'lucide-react';

const LockerConfigManager = ({ eventId }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [manualNumbers, setManualNumbers] = useState('');

  const { data: summary, isLoading } = useQuery({
    queryKey: ['lockerSummary', eventId],
    queryFn: () => ApiService.getLockerSummary(eventId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries(['lockerSummary', eventId]);
    queryClient.invalidateQueries(['availableLockers', eventId]);
  };

  const configureMutation = useMutation({
    mutationFn: (payload) => ApiService.configureLockers(eventId, payload),
    onSuccess: (res) => {
      showSuccess(`Added ${res?.added ?? 0} locker(s).`);
      invalidate();
    },
    onError: (e) => showError(e.message || 'Failed to add lockers'),
  });

  const importMutation = useMutation({
    mutationFn: (file) => ApiService.importLockersCsv(eventId, file),
    onSuccess: (res) => {
      showSuccess(`Imported ${res?.added ?? 0} locker(s) from CSV.`);
      invalidate();
    },
    onError: (e) => showError(e.message || 'Failed to import CSV'),
  });

  const handleAddRange = () => {
    const from = parseInt(rangeFrom, 10);
    const to = parseInt(rangeTo, 10);
    if (Number.isNaN(from) || Number.isNaN(to)) {
      showError('Enter valid numeric range values');
      return;
    }
    if (from > to) {
      showError('"From" must be less than or equal to "To"');
      return;
    }
    configureMutation.mutate({ rangeFrom: from, rangeTo: to });
    setRangeFrom('');
    setRangeTo('');
  };

  const handleAddManual = () => {
    const numbers = parseManualNumbers(manualNumbers);
    if (numbers.length === 0) {
      showError('Enter one or more locker numbers');
      return;
    }
    configureMutation.mutate({ numbers });
    setManualNumbers('');
  };

  const handleCsv = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      importMutation.mutate(file);
    }
    e.target.value = '';
  };

  const busy = configureMutation.isPending || importMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total', value: summary?.total },
          { label: 'Available', value: summary?.available },
          { label: 'Assigned', value: summary?.assigned },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border p-4 text-center">
            <div className="text-2xl font-semibold">{isLoading ? '—' : (s.value ?? 0)}</div>
            <div className="text-sm text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Range */}
      <div className="space-y-2">
        <Label>Add by range</Label>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs font-normal text-gray-500">From</Label>
            <Input type="number" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} placeholder="1" />
          </div>
          <div className="flex-1">
            <Label className="text-xs font-normal text-gray-500">To</Label>
            <Input type="number" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} placeholder="100" />
          </div>
          <Button onClick={handleAddRange} disabled={busy}>
            <Plus className="h-4 w-4 mr-1" /> Add range
          </Button>
        </div>
      </div>

      {/* Manual */}
      <div className="space-y-2">
        <Label>Add manually</Label>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              value={manualNumbers}
              onChange={(e) => setManualNumbers(e.target.value)}
              placeholder="e.g. 101, 102, A5 (comma or space separated)"
            />
          </div>
          <Button onClick={handleAddManual} disabled={busy}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* CSV */}
      <div className="space-y-2">
        <Label>Import from CSV</Label>
        <p className="text-xs text-gray-500">
          A single column of locker numbers (numeric or alphanumeric, e.g. A5). A header row like
          "locker" or "number" is ignored.
        </p>
        <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border px-4 py-2 text-sm hover:bg-gray-50">
          <Upload className="h-4 w-4" />
          <span>{importMutation.isPending ? 'Uploading…' : 'Choose CSV file'}</span>
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleCsv} disabled={busy} />
        </label>
      </div>
    </div>
  );
};

export default LockerConfigManager;
